// Strips Intacct's [Support ID: ...] noise from any string
const stripSupportId = (s) => (s || '').replace(/\s*\[Support ID:[^\]]+\]/g, '').trim();

// Extracts a human-readable message from a single Intacct error object.
// Always prefer description2 if it has real content after stripping Support ID noise,
// then fall back to errorno (which is sometimes itself the human-readable message),
// then description.
const extractSingleError = (err) => {
  if (!err) return null;
  const desc2 = stripSupportId(err.description2);
  if (desc2) return desc2;
  const errorno = stripSupportId(err.errorno);
  if (errorno) return errorno;
  return stripSupportId(err.description) || 'Failed';
};

// Extracts error message from various Intacct response formats
export const extractErrorMessage = (res) => {
  if (res.status === 'success') return 'Success';
  if (res.errormessage) {
    const err = res.errormessage.error;
    if (Array.isArray(err)) {
      return extractSingleError(err[0]) || 'Failed';
    } else if (err) {
      return extractSingleError(err) || 'Failed';
    } else if (typeof res.errormessage === 'string') {
      return res.errormessage;
    }
  }
  return 'Failed';
};

// Writes results column to Excel for standard record operations
export const writeResultsToExcel = async (results) => {
  await Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("rowCount, columnCount");
    await context.sync();
    
    const resultHeader = range.getCell(0, range.columnCount);
    resultHeader.values = [["Result"]];
    resultHeader.format.font.bold = true;
    
    if (results.length > 0) {
      const dataRange = range.getCell(1, range.columnCount).getResizedRange(results.length - 1, 0);
      dataRange.values = results.map(res => [extractErrorMessage(res)]);
    }
    
    range.getResizedRange(0, 1).format.autofitColumns();
    await context.sync();
  });
};

// Writes results column for grouped operations (rate tables, documents, PO creates)
export const writeGroupedResultsToExcel = async (results, records, keyField) => {
  await Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("rowCount, columnCount");
    await context.sync();
    
    const resultHeader = range.getCell(0, range.columnCount);
    resultHeader.values = [["Result"]];
    resultHeader.format.font.bold = true;
    
    // Build result map by key field
    const resultMap = {};
    if (results && results.length > 0) {
      results.forEach(res => {
        const key = res.controlid || res.key;
        resultMap[key] = extractErrorMessage(res);
      });
    }
    
    // Apply results to each row
    const dataRows = records.length;
    if (dataRows > 0) {
      const resultValues = records.map(rec => {
        const key = rec[keyField] || rec[keyField.toLowerCase()];
        return [resultMap[key] || 'Processed'];
      });
      const dataRange = range.getCell(1, range.columnCount).getResizedRange(dataRows - 1, 0);
      dataRange.values = resultValues;
    }
    
    range.getResizedRange(0, 1).format.autofitColumns();
    await context.sync();
  });
};